import React, { Fragment, ReactElement } from 'react';
import { connect, MapStateToPropsParam } from 'react-redux';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import { track } from '@fenderdigital/utils/analytics';
import Button from '@fenderdigital/ui/Button';
import { Download2 as DownloadIcon } from '@fenderdigital/ui/Icons';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import AppStoreButton from '@fenderdigital/react-ui-kit/app-store-button';
import Image, { getVariantUrls } from '@fenderdigital/react-ui-kit/image';
import { Course, Lesson, LessonTonePreset } from '@fenderdigital/api-definitions/play';
import { trackEvent, EventNames } from '../../../lib/analytics';
import { getTimezone, getLocale } from '../../../utils/date-utils';
import UserAgent from '../../../lib/user-agent';
import {
  Presets,
  Preset,
  PresetIconContainer,
  PresetIcon,
  PresetMetadata,
  PresetTitle,
  PresetDescription,
  MobileDownloadButton,
  PresetDivider,
  Container,
  Title,
  InstructionItem,
  InstructionCount,
  InstructionText,
  StartPlaying,
  ContainerFooter,
  Link,
  AmpsRow,
  Amp,
  ToneAppContainer,
  ToneAppHeader,
  ToneAppIcon,
  ToneAppMetadata,
  ToneAppTitle,
  ToneAppDescription,
  ToneAppBody,
  ToneAppUpsellImage,
  ToneAppUpsellContent,
  AppStoreButtons,
} from './LessonToneStyledComponents';

interface LessonToneProps {
  course: Course;
  lesson: Lesson;
  presets: LessonTonePreset[];
}

interface MappedProps {
  courses: any;
}

const LessonTone = ({
  course,
  courses,
  lesson,
  presets,
}: MappedProps & LessonToneProps): ReactElement => {
  const { user } = usePlayUser();
  const isMobile = UserAgent.isMobileOrTabletUAString();
  const downloadPreset = (): void => {
    const currentCourse = courses?.watching?.currentCourse || course;
    const currentLesson = courses?.watching?.currentLesson || lesson;
    /* eslint-disable @typescript-eslint/camelcase */
    const trackingProperties = {
      fc_id: user?.userID,
      lesson_id: currentLesson?.id,
      lesson_slug: currentLesson?.slug,
      lesson_type_1: currentLesson?.type,
      lesson_name: currentLesson?.title,
      course_id: currentCourse?.id,
      course_slug: currentCourse?.slug,
      course_name: currentCourse?.title,
      timezone: getTimezone(),
      preset_id: presets?.[0]?.id,
    };
    /* eslint-enable @typescript-eslint/camelcase */

    const legacyProperties = {
      platform: 'web',
      locale: getLocale(),
    };
    trackEvent(EventNames.PRESET_DOWNLOADED, trackingProperties, legacyProperties, true, true);
  };

  const handleDownloadClick = (id: string): void => {
    const url = isMobile
      ? `https://www.fender.com/tone/presets/${id}`
      : 'https://www.fender.com/products/guitar-amplifiers/contemporary-digital/mustang-gtx';
    downloadPreset();
    window.location.href = url;
  };
  const amps = [
    {
      name: 'Mustang GTX 50',
      url: 'https://stuff.fendergarage.com/images/a/R/m/amp-gtx-50.png',
    },
    {
      name: 'Mustang GTX 100',
      url: 'https://stuff.fendergarage.com/images/O/x/W/amp-gtx-100.png',
    },
    {
      name: 'Mustang GT 40',
      url: 'https://stuff.fendergarage.com/images/G/l/O/amp-gt-40.png',
    },
    {
      name: 'Mustang GT 100',
      url: 'https://stuff.fendergarage.com/images/J/O/1/amp-gt-100-200.png',
    },
    {
      name: 'Mustang GT 200',
      url: 'https://stuff.fendergarage.com/images/J/O/1/amp-gt-100-200.png',
    },
  ];

  const isTabletPortraitUp = useBreakpoint(up('tablet-portrait'));
  return (
    <>
      <p className="f6">
        Download preset(s) from this lesson to your Mustang&trade; GT/GTX amplifier.
      </p>
      <Presets>
        {presets.map(({ name, description, id }, index) => (
          <Fragment key={`Tone Preset - ${name}`}>
            <Preset>
              <PresetIconContainer>
                <PresetIcon>
                  <Image
                    alt={name}
                    bgColor="transparent"
                    images={getVariantUrls(
                      'https://stuff.fendergarage.com/images/T/b/a/preset-icon.png',
                      1,
                    )}
                    ratio={1}
                  />
                </PresetIcon>
              </PresetIconContainer>
              <PresetMetadata>
                <PresetTitle>{name}</PresetTitle>
                {description && <PresetDescription>{description}</PresetDescription>}
              </PresetMetadata>
              {isTabletPortraitUp ? (
                <Button
                  onClick={(): void => handleDownloadClick(id)}
                  dataId="download-button"
                  shape="pill"
                  leftIcon={<DownloadIcon fill="#fff" width=".4em" height=".9em" />}
                >
                  Download Preset
                </Button>
              ) : (
                <MobileDownloadButton
                  onClick={(): void => handleDownloadClick(id)}
                  data-id="download-button"
                >
                  <DownloadIcon fill="#fff" width=".4em" height=".9em" />
                </MobileDownloadButton>
              )}
            </Preset>
            {index !== presets.length - 1 ? <PresetDivider /> : null}
          </Fragment>
        ))}
      </Presets>
      <Container>
        <Title>How to download presets to your Mustang GT/GTX</Title>
        <InstructionItem>
          <InstructionCount>1</InstructionCount>
          <InstructionText>
            <strong>Things you&apos;ll need:</strong> Fender Tone app for{' '}
            <Link href="https://apps.apple.com/us/app/fender-tone/id1174113426" target="_blank">
              iOS
            </Link>{' '}
            or{' '}
            <Link
              href="https://play.google.com/store/apps/details?id=com.fender.tone&hl=en_US"
              target="_blank"
            >
              Android
            </Link>
            , Mustang GT or GTX amp, an Internet connection
          </InstructionText>
        </InstructionItem>
        <InstructionItem>
          <InstructionCount>2</InstructionCount>
          <InstructionText>
            Turn on your <strong>Mustang GT</strong> or <strong>GTX.</strong>
          </InstructionText>
        </InstructionItem>
        <InstructionItem>
          <InstructionCount>3</InstructionCount>
          <InstructionText>
            From your mobile device click the <strong>Download</strong> button above these
            directions
          </InstructionText>
        </InstructionItem>
        <InstructionItem>
          <InstructionCount>4</InstructionCount>
          <InstructionText>
            The <strong>Fender Tone</strong> app, once installed on your device, will open to the
            Preset
          </InstructionText>
        </InstructionItem>
        <InstructionItem>
          <InstructionCount>5</InstructionCount>
          <InstructionText>
            Click the <strong>ADD</strong> button to add it to your amp
          </InstructionText>
        </InstructionItem>
        <StartPlaying>Start playing!</StartPlaying>
        <ContainerFooter>
          <p style={{ marginBottom: 8 }} className="f6">
            <strong>Need more help?</strong>
          </p>
          <Link
            href="https://tone-support.fender.com/hc/en-us/articles/115003517603-How-do-I-browse-and-download-new-Tone-presets-"
            target="_blank"
          >
            Learn more about downloading presets
          </Link>
        </ContainerFooter>
      </Container>
      <Container>
        <Title>Mustang GT/GTX Amps</Title>
        <AmpsRow>
          {amps.slice(0, 2).map(({ name, url }) => (
            <Amp key={name}>
              <Image alt={name} bgColor="transparent" images={getVariantUrls(url, 1)} ratio={1} />
              <strong>{name}</strong>
            </Amp>
          ))}
        </AmpsRow>
        <AmpsRow>
          {amps.slice(2, 5).map(({ name, url }) => (
            <Amp className={name === 'Mustang GT 200' ? 'GT-200' : ''} key={name}>
              <Image alt={name} bgColor="transparent" images={getVariantUrls(url, 1)} ratio={1} />
              <strong>{name}</strong>
            </Amp>
          ))}
        </AmpsRow>
        <ContainerFooter>
          <Link
            href="https://shop.fender.com/en-US/tone-enabled-amplifiers/"
            target="_blank"
            data-id="shop-link"
          >
            See All GT/GTX Amps
          </Link>
        </ContainerFooter>
      </Container>
      <ToneAppContainer>
        <ToneAppHeader>
          <ToneAppIcon>
            <Image
              alt="Fender Tone"
              bgColor="transparent"
              images={getVariantUrls(
                'https://stuff.fendergarage.com/images/a/K/5/fender-tone-app-icon.png',
                1,
              )}
              ratio={1}
            />
          </ToneAppIcon>
          <ToneAppMetadata>
            <ToneAppTitle>Fender Tone&trade;</ToneAppTitle>
            <ToneAppDescription>
              Control. Edit. Download. For Mustang GT and GTX amplifiers.
            </ToneAppDescription>
          </ToneAppMetadata>
        </ToneAppHeader>
        <ToneAppBody>
          <ToneAppUpsellImage>
            <Image
              alt="Fender Tone"
              bgColor="transparent"
              images={getVariantUrls(
                'https://stuff.fendergarage.com/images/h/q/Y/fender-tone-promo.png',
                1,
              )}
              ratio={187 / 345}
            />
          </ToneAppUpsellImage>
          <ToneAppUpsellContent>
            <ul style={{ listStyleType: 'disc', paddingLeft: '40px' }}>
              <li>Easy Editing</li>
              <li>Real-time Control</li>
              <li>Preset Sounds</li>
              <li>Browse Artist Presets</li>
            </ul>
            <AppStoreButtons data-id="app-links">
              <AppStoreButton
                variant="apple"
                className="blue mr2"
                link="https://apps.apple.com/us/app/fender-tone/id1174113426"
              />
              <AppStoreButton
                variant="google"
                className="blue"
                link="https://play.google.com/store/apps/details?id=com.fender.tone&hl=en_US"
              />
            </AppStoreButtons>
          </ToneAppUpsellContent>
        </ToneAppBody>
      </ToneAppContainer>
    </>
  );
};

const mapState: MapStateToPropsParam<MappedProps, LessonToneProps, any> = ({ courses }) => ({
  courses,
});

export default connect(mapState)(LessonTone);
